import {assert} from 'Asserts';
import {SampleDetails} from 'constants/SampleConstants';
import {LeafType, isBenchlingTypeLeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import inferTypeFromLookupConfig, {InferredLookupConfigType} from 'lookup-config/inferTypeFromLookupConfig';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import {SampleFilter, SampleFilterType} from 'notebook/sample-propagation/SampleFilter';
import {Schema} from 'schemas/SchemaConstants';

const TYPE_DEF_TO_SAMPLE_FILTER_TYPE: Map<LeafType, SampleFilterType> = new Map([
  [LeafType.BASIC_FOLDER_ITEM, SampleFilterType.BIOENTITY],
  [LeafType.BATCH, SampleFilterType.BATCH],
  [LeafType.BIOENTITY, SampleFilterType.BIOENTITY],
  [LeafType.BOOLEAN, SampleFilterType.BOOLEAN],
  [LeafType.BOX, SampleFilterType.BOX],
  [LeafType.CONTAINER, SampleFilterType.CONTAINER],
  [LeafType.DECIMAL, SampleFilterType.FLOAT],
  [LeafType.INTEGER, SampleFilterType.INTEGER],
  [LeafType.PLATE, SampleFilterType.PLATE],
  [LeafType.STORABLE, SampleFilterType.STORABLE],
  [LeafType.TEXT, SampleFilterType.STRING],
]);

// Given an example of a sample, we determine the approriate type of SampleFilter
export default function getAppropriateSampleFilter(sampleDetails: SampleDetails): SampleFilter {
  if (sampleDetails.batchInfo != null) {
    return {type: SampleFilterType.BATCH};
  } else if (sampleDetails.bioentityInfo != null) {
    return {type: SampleFilterType.BIOENTITY};
  } else if (sampleDetails.boxInfo != null) {
    return {type: SampleFilterType.BOX};
  } else if (sampleDetails.containerInfo != null) {
    return {type: SampleFilterType.CONTAINER};
  } else if (sampleDetails.textInfo != null) {
    return {type: SampleFilterType.STRING};
  } else if (sampleDetails.booleanInfo != null) {
    return {type: SampleFilterType.BOOLEAN};
  } else if (sampleDetails.numberInfo != null) {
    return {type: sampleDetails.numberInfo.isFloat ? SampleFilterType.FLOAT : SampleFilterType.INTEGER};
  }
  assert(sampleDetails.plateInfo != null, 'Sample must be of type plate.');
  return {type: SampleFilterType.PLATE};
}

/**
 * Determines the sample filter that matches the type of items returned by the provided lookup config.
 * Returns null if the type of items is incompatible with sample filters, or if the schemas used by the
 * lookup config are not available in Apollo's GraphQL cache.
 *
 * @param lookupConfig The lookup config
 * @param baseSchema The schema the lookup config starts from
 * @returns The SampleFilter matching the lookup config, null if there is no matching SampleFilter, or
 * undefined if the schemas are not yet resolved to infer the Sample Filter
 */
export function inferSampleFilterFromLookupConfig(
  lookupConfig: LookupConfigSpec,
  baseSchema: Schema
): SampleFilter | null | undefined {
  let inferredType: InferredLookupConfigType;
  try {
    inferredType = inferTypeFromLookupConfig(lookupConfig, baseSchema);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (err) {
    // Fail gracefully when the type inference fails since the schemas have not been loaded into cache yet.
    return undefined;
  }

  if (
    isBenchlingTypeLeafType(inferredType.typeDef) &&
    TYPE_DEF_TO_SAMPLE_FILTER_TYPE.has(inferredType.typeDef)
  ) {
    return {
      type: TYPE_DEF_TO_SAMPLE_FILTER_TYPE.get(inferredType.typeDef)!,
      schemaId: inferredType.linkSchema?.id ?? null,
    };
  }

  return null;
}
